"use client";

import Image from "next/image";
import Link from "next/link";
import ImageFallback from "@/app/components/ui/image-fallback";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

export default function CampaignCard({
  image,
  title,
  subtitle,
  campaignTypes,
  href,
}: {
  image?: string;
  title: string;
  subtitle: string;
  campaignTypes: string[];
  href?: string;
}) {
  const { isSkoda, isAudi } = useBrand();
  const skodaCardClass =
    "rounded-none border-0 bg-white text-black shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)] hover:bg-white";
  const audiCardClass =
    "rounded-[20px] border-0 bg-[#1D2129] text-white shadow-none hover:bg-[#252b36]";

  // Plain JSX value (not an inline component) so React does not remount the card subtree on every render
  const cardContent = (
    <div
      className={cn(
        "bg-background flex h-full w-full min-w-[260px] flex-col overflow-hidden rounded-lg border text-black transition-colors hover:bg-[#e9e9e9]",
        isSkoda && skodaCardClass,
        isAudi && audiCardClass
      )}
    >
      {image ? (
        <Image
          src={image}
          alt={title}
          width={600}
          height={338}
          className={cn(
            "aspect-[16/9] w-full rounded-t-lg object-cover",
            isSkoda && "rounded-none",
            isAudi && "rounded-t-[20px]"
          )}
        />
      ) : (
        <ImageFallback
          className={cn(
            "aspect-[16/9] rounded-t-lg",
            isSkoda && "rounded-none",
            isAudi && "rounded-t-[20px]"
          )}
        />
      )}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-row items-center justify-start gap-2">
          <h3
            className={cn("heading-small text-black", isAudi && "text-white")}
          >
            {title}
          </h3>
          {campaignTypes?.map((type) => (
            <div
              key={type}
              className={cn(
                "bg-light-blue rounded-sm px-1 py-0.5 text-xs",
                isAudi && "bg-[#2d3340]"
              )}
            >
              <span
                className={cn("font-medium text-black", isAudi && "text-white")}
              >
                {type}
              </span>
            </div>
          ))}
        </div>
        <p
          className={cn(
            "text-muted-foreground min-h-[2.5rem] flex-1 p-0 pt-1 text-sm",
            isSkoda && "text-black",
            isAudi && "text-[#a0a8b4]"
          )}
        >
          {subtitle}
        </p>
        <div
          className={cn(
            "pt-2 font-semibold text-black",
            isAudi && "text-white"
          )}
        >
          &nbsp;
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={cn(
          "block",
          isSkoda && "text-black hover:text-black",
          isAudi && "text-white hover:text-white"
        )}
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
